<template>
  <div class="">
    <button class="btn-indigo w-32 font-normal" @click="openModal()">Voicemail</button>
    <ModalHeadlessUI ref="modal">
      <template #default>
        <h1 class="font-bold mb-4 text-3xl text-center">
          Send Voicemail Drop
        </h1>
        <div class="mb-6">
          <VoiceRecorder 
            :showManageRecordings="showManageRecordings"
            :recordings="userRecordings"
            @recordingSaved="fetchRecordings"
            @recordingDeleted="handleRecordingDeleted"
            @recordingSelected="handleRecordingSelected"
            @playbackStateChanged="handlePlaybackStateChanged"
            ref="voiceRecorder"
          />
        </div>
        
        <form id="voicemailForm" name="voicemailForm" @submit.prevent="sendVoicemail">
          <div class="mx-auto max-w-sm text-center flex flex-wrap justify-center">
            <div class="flex items-center mr-2 mb-4">
              <input id="borrowerPhone" v-model="voicemailForm.sendTo" v-bind:value="phone" type="radio" name="sendTo" class="rounded-full h-4 w-4 border border-gray-300 bg-white checked:bg-indigo-500 checked:border-indigo-500 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer" checked />
              <label for="borrowerPhone" class="flex items-center text-sm font-light cursor-pointer">Borrower: {{ phone }}</label>
            </div>
            <div v-if="phoneCoborrower" class="flex items-center mr-2 mb-4">
              <input id="coBorrowerPhone" v-model="voicemailForm.sendTo" v-bind:value="phoneCoborrower" type="radio" name="sendTo" class="rounded-full h-4 w-4 border border-gray-300 bg-white checked:bg-indigo-500 checked:border-indigo-500 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer" />
              <label for="coBorrowerPhone" class="flex items-center text-sm font-light cursor-pointer">Co-Borrower: {{ phoneCoborrower }}</label>
            </div>
            <div v-if="voicemailForm.errors.sendTo" class="form-error">{{ voicemailForm.errors.sendTo }}</div>
          </div>
          <div class="relative">
            <select-input 
              v-model="voicemailTemplate" 
              class="pb-4 w-full" 
              label="Select a Recording to Send" 
              @change="onTemplateChange($event)"
            >
              <option value="">Select a recording</option>
              <template v-for="recording in userRecordings" :key="recording.id">
                <option :value="recording.file_path">{{ recording.name }}</option>
              </template>
            </select-input>
            
            <!-- Manage recordings toggle button -->
            <button 
              type="button"
              @click="showManageRecordings = !showManageRecordings"
              class="text-sm text-indigo-600 hover:text-indigo-800 transition-colors flex items-center gap-1 mb-2"
            >
              <i :class="showManageRecordings ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
              Manage Recordings
            </button>

            <!-- Recordings list -->
            <div v-if="showManageRecordings" class="mt-2 space-y-2 max-h-60 overflow-y-auto">
              <div v-for="recording in userRecordings" 
                   :key="recording.id"
                   class="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span class="text-sm text-gray-700">{{ recording.name }}</span>
                <div class="flex items-center gap-3">
                  <button 
                    @click.prevent="$refs.voiceRecorder?.togglePlay(recording)"
                    class="text-indigo-600 hover:text-indigo-800 transition-colors"
                    :title="currentlyPlayingId === recording.id ? 'Stop' : 'Play'"
                  >
                    <i :class="currentlyPlayingId === recording.id ? 'fas fa-stop-circle text-2xl' : 'fas fa-play-circle text-2xl'"></i>
                  </button>
                  <button 
                    @click.prevent="$refs.voiceRecorder?.deleteRecording(recording.id)"
                    class="text-red-600 hover:text-red-800 transition-colors"
                    title="Delete recording"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="voicemailForm.errors.template" class="form-error">{{ voicemailForm.errors.template }}</div>
          <div class="flex justify-center items-center mt-5">
            <div>
              <loading-button :loading="voicemailForm.processing" class="btn-indigo ml-auto" type="submit">Send Voicemail</loading-button>
            </div>
          </div>
        </form>
      </template>
    </ModalHeadlessUI>
  </div>
</template>

<script>
import ModalHeadlessUI from '@/Shared/ModalHeadlessUI'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import VoiceRecorder from '@/Shared/VoiceRecorder'
import { router } from '@inertiajs/vue3'

export default {
  components: {
    ModalHeadlessUI,
    SelectInput,
    LoadingButton,
    VoiceRecorder,
  },
  props: {
    auth: {
      type: Object,
      default: null,
    },
    id: {
      type: Number,
      default: null,
    },
    phone: {
      type: String,
      default: '',
    },
    phoneCoborrower: {
      type: String,
      default: '',
    },
    source: {
      type: String,
      default: 'lead', // Can be 'lead' or 'contact'
    }
  },
  data() {
    return {
      showManageRecordings: false,
      userRecordings: [],
      voicemailForm: router.form({
        id: this.id,
        source: this.source,
        sendTo: this.phone,
        sendFrom: this.auth.user.phone,
        template: '',
        recordingName: '',
      }),
      voicemailTemplate: '',
      currentlyPlayingId: null,
    }
  },
  mounted() {
    this.fetchRecordings()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods:{
    openModal() {
      this.$refs.modal.openModal()
    },
    closeModal() {
      const audioPlayer = this.$refs.audioPlayer
      if (audioPlayer) {
        audioPlayer.pause()
        audioPlayer.currentTime = 0
      }
      this.$refs.modal?.closeModal()
      this.voicemailForm.reset()
      this.voicemailTemplate = ''
    },
    onTemplateChange(event) {
      const selectedValue = event.target.value
      this.voicemailTemplate = selectedValue || ''
      const selectedRecording = this.userRecordings.find(recording => recording.file_path === selectedValue);
      this.voicemailForm.recordingName = selectedRecording ? selectedRecording.name : '';
    },
    sendVoicemail() {
      this.voicemailForm.template = this.voicemailTemplate;
      this.voicemailForm.source = this.source;
      
      this.voicemailForm.post('/voicemail', {
        onSuccess: () => {
          this.voicemailForm.reset('sendTo');
          this.voicemailForm.reset('template');
          this.voicemailForm.reset('recordingName');
          this.closeModal();
        },
      });
    },
    async fetchRecordings() {
      try {
        const response = await fetch('/voicemail/getRecordings', {
          headers: {
            'Accept': 'application/json'
          }
        });
        const data = await response.json();
        this.userRecordings = data.recordings;
      } catch (error) {
        console.error('Error fetching recordings:', error.message || error);
      }
    },
    handleRecordingDeleted(id) {
      this.userRecordings = this.userRecordings.filter(r => r.id !== id);
      if (this.voicemailForm.template === this.voicemailTemplate) {
        this.voicemailForm.template = '';
        this.voicemailTemplate = '';
      }
    },
    handleRecordingSelected(recording) {
      this.voicemailTemplate = recording.file_path;
      this.voicemailForm.template = recording.file_path;
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        this.closeModal()
      } else if (document.visibilityState === 'visible') {
        this.fetchRecordings()
      }
    },
    handlePlaybackStateChanged(recording) {
      this.currentlyPlayingId = recording ? recording.id : null;
    },
  },
}
</script>

<style scoped>
.recordings-list {
  max-height: 200px;
  overflow-y: auto;
}
</style>
